/**
 * Prefer a single domain_mappings row when apex + www (or duplicates) exist.
 * Never use bare `.maybeSingle()` on slug/user_id — multiple rows throw
 * "JSON object requested, multiple (or no) rows returned".
 */

export type DomainMappingRow = {
  domain?: string | null;
  status?: string | null;
  is_active?: boolean | null;
  updated_at?: string | null;
  created_at?: string | null;
  [key: string]: unknown;
};

const STATUS_RANK: Record<string, number> = {
  active: 0,
  verified: 1,
  pending: 2,
};

function statusRank(row: DomainMappingRow): number {
  const s = String(row.status || "").trim().toLowerCase();
  if (s && s in STATUS_RANK) return STATUS_RANK[s];
  if (row.is_active === true) return STATUS_RANK.active;
  return 50;
}

function isApexDomain(domain?: string | null): boolean {
  const d = String(domain || "")
    .trim()
    .toLowerCase();
  return Boolean(d) && !d.startsWith("www.");
}

function recencyMs(row: DomainMappingRow): number {
  const raw = row.updated_at || row.created_at || "";
  const t = Date.parse(String(raw));
  return Number.isFinite(t) ? t : 0;
}

/** Pick the best mapping row: active > verified > pending, apex over www, then newest. */
export function pickBestDomainMapping<T extends DomainMappingRow>(
  rows: T[] | null | undefined,
): T | null {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const sorted = [...rows].sort((a, b) => {
    const byStatus = statusRank(a) - statusRank(b);
    if (byStatus !== 0) return byStatus;
    const aApex = isApexDomain(a.domain) ? 0 : 1;
    const bApex = isApexDomain(b.domain) ? 0 : 1;
    if (aApex !== bApex) return aApex - bApex;
    return recencyMs(b) - recencyMs(a);
  });
  return sorted[0] ?? null;
}

/** True for the classic PostgREST multi/zero-row `.single()` / `.maybeSingle()` failure. */
export function isPostgrestSingleRowError(message?: string | null): boolean {
  const m = String(message || "").toLowerCase();
  return (
    m.includes("json object requested") ||
    m.includes("multiple (or no) rows") ||
    m.includes("results contain 0 rows") ||
    m.includes("results contain more than one")
  );
}
